import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export const inputClassName = (hasError) => classNames(
  'w-full bg-white border rounded-cs-6 px-4 py-2.5',
  'text-base text-cs-gray-900 placeholder:text-cs-gray-400',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-1',
  'disabled:bg-cs-gray-50 disabled:text-cs-gray-500',
  hasError ? 'border-red-500' : 'border-cs-gray-300'
);

/**
 * Label + control + help/error text. The control is rendered by the caller
 * via a render prop that receives the ids/aria attributes to spread on it,
 * so inputs, textareas and selects all get the same labelling.
 */
FormRow.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  optionalLabel: PropTypes.node,
  required: PropTypes.bool,
  help: PropTypes.node,
  error: PropTypes.node,
  children: PropTypes.func.isRequired
};

export default function FormRow(props) {
  const { id, label, optionalLabel, required = false, help, error, children } = props;
  const helpId = help ? `${id}-help` : null;
  const errorId = error ? `${id}-error` : null;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-semibold text-cs-gray-900">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
        {!required && optionalLabel && (
          <span className="ml-1 font-normal text-cs-gray-500">{optionalLabel}</span>
        )}
      </label>
      {children({
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined
      })}
      {error && (
        <p id={errorId} className="text-sm text-red-600">{error}</p>
      )}
      {help && (
        <p id={helpId} className="text-sm text-cs-gray-500">{help}</p>
      )}
    </div>
  );
}
